import React, { useState, useMemo, useDeferredValue, useCallback } from 'react';
import { collection, addDoc, updateDoc, doc, deleteDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { db, analytics } from '../firebase';
import useGardenData from '../hooks/useGardenData';
import { createLogEntry } from '../services/logService';
import { useUpload } from '../contexts/UploadContext';
import { getLocalDateString } from '../utils/dateUtils';

import {
    Button,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Stack,
    Tabs,
    Tab,
} from '@mui/material';

import GardenOverview from './dashboard/GardenOverview';
import SeasonalityInput from './SeasonalityInput';
import SeedSuggestions from './dashboard/SeedSuggestions';
import { Seed } from '../types/shared';
import {
    Search,
    AutoAwesome
} from '@mui/icons-material';
import {
    InputAdornment,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingScreen } from './shared/Loading';

interface SeedFormData {
    name: string;
    variety: string;
    notes: string;
    plantIndoor: { [key: number]: boolean };
    plantOutdoors: { [key: number]: boolean };
    harvest: { [key: number]: boolean };
    bestBefore: string | null;
    supplier: string;
    count: string; // empty string = unknown
    rawInputText: string;
    imageFile: File | null;
    imageUrl: string | null;
}

interface PlantingFormData {
    spaceId: string;
    count: number | string;
    plantingDate: string;
    stage: string;
}

const SeedsPage: React.FC = () => {
    const { t } = useTranslation();
    const { user, seeds, spaces, loading: dataLoading } = useGardenData();
    const { uploadFile } = useUpload();

    // State
    const [showSeedForm, setShowSeedForm] = useState(false);
    const [editingSeed, setEditingSeed] = useState<Seed | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showPlantingForm, setShowPlantingForm] = useState(false);
    const [plantingSeed, setPlantingSeed] = useState<Seed | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [seedToDelete, setSeedToDelete] = useState<Seed | null>(null);
    const [showPostPlantDialog, setShowPostPlantDialog] = useState(false);
    const [postPlantSeed, setPostPlantSeed] = useState<Seed | null>(null);

    const [seedFormData, setSeedFormData] = useState<SeedFormData>({
        name: '',
        variety: '',
        notes: '',
        plantIndoor: {},
        plantOutdoors: {},
        harvest: {},
        bestBefore: null,
        supplier: '',
        count: '',
        rawInputText: '',
        imageFile: null,
        imageUrl: null,
    });

    const [plantingData, setPlantingData] = useState<PlantingFormData>({
        spaceId: '',
        count: 1,
        plantingDate: getLocalDateString(),
        stage: 'Seedling'
    });

    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [seedTab, setSeedTab] = useState(0);

    // Filtering state
    const [searchQuery, setSearchQuery] = useState('');
    const deferredSearchQuery = useDeferredValue(searchQuery);

    const getCurrentMonthIndex = useCallback(() => {
        return new Date().getMonth() + 1; // 1-12
    }, []);

    const isInSeason = useCallback((seed: Seed) => {
        const currentMonth = getCurrentMonthIndex();
        const indoor = seed.plantIndoor && seed.plantIndoor[currentMonth];
        const outdoor = seed.plantOutdoors && seed.plantOutdoors[currentMonth];
        return !!(indoor || outdoor);
    }, [getCurrentMonthIndex]);

    const resetSeedForm = () => {
        setSeedFormData({
            name: '',
            variety: '',
            notes: '',
            plantIndoor: {},
            plantOutdoors: {},
            harvest: {},
            bestBefore: null,
            supplier: '',
            count: '',
            rawInputText: '',
            imageFile: null,
            imageUrl: null,
        });
        setEditingSeed(null);
        setSeedTab(0);
        setShowSeedForm(false);
    };

    const handleAddSuggestedSeed = async (seed: Partial<Seed>) => {
        if (!user) return;
        try {
            const docRef = await addDoc(collection(db, 'seeds'), {
                ...seed,
                userId: user.uid,
                createdAt: new Date(),
                processed: true
            });
            logEvent(analytics, 'seed_created', { seed_id: docRef.id, name: seed.name || '', variety: seed.variety || '' });
        } catch (error) {
            console.error("Error adding suggested seed:", error);
            alert(t('seeds.error_add'));
        }
    };

    const handleSeedSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        const isAIAutoFill = seedTab === 0 && (seedFormData.rawInputText || seedFormData.imageFile || (!editingSeed && seedFormData.imageUrl));

        const seedData: any = {
            ...seedFormData,
            name: seedFormData.name || '',
            variety: seedFormData.variety || '',
            userId: user.uid,
            updatedAt: new Date(),
            imageFile: null,
            rawInput: {
                text: seedFormData.rawInputText || '',
                imageUrl: seedFormData.imageUrl || null,
                storagePath: null
            },
            processed: !isAIAutoFill
        };

        // Handle count: empty string means unknown (delete field), otherwise parse as number
        if (seedFormData.count === '' || seedFormData.count === undefined) {
            delete seedData.count;
        } else {
            seedData.count = Number(seedFormData.count);
        }

        delete seedData.rawInputText;

        if (seedData.bestBefore && typeof seedData.bestBefore === 'string') {
            seedData.bestBefore = new Date(seedData.bestBefore);
        } else if (!seedData.bestBefore) {
            seedData.bestBefore = null;
        }

        try {
            let seedId = '';
            if (editingSeed) {
                seedId = editingSeed.id;
                await updateDoc(doc(db, 'seeds', seedId), seedData);
                logEvent(analytics, 'seed_updated', { seed_id: seedId });
                setEditingSeed(null);
            } else {
                const docRef = await addDoc(collection(db, 'seeds'), {
                    ...seedData,
                    createdAt: new Date(),
                    // Start as processed: true if there's an image to upload, then set to false once upload done
                    processed: seedFormData.imageFile ? true : !isAIAutoFill
                });
                seedId = docRef.id;
                logEvent(analytics, 'seed_created', { seed_id: seedId, name: seedData.name, variety: seedData.variety });
            }

            // Background upload if imageFile exists
            if (seedFormData.imageFile) {
                const file = seedFormData.imageFile;
                const path = `seeds/${user.uid}/${Date.now()}_${file.name}`;

                uploadFile(seedId, path, file, async (imageUrl, storagePath) => {
                    await updateDoc(doc(db, 'seeds', seedId), {
                        'rawInput.imageUrl': imageUrl,
                        'rawInput.storagePath': storagePath,
                        processed: false, // Now trigger AI processing
                        updatedAt: new Date()
                    });
                });
            }

            resetSeedForm();
        } catch (error) {
            console.error("Error saving seed:", error);
            alert(t('seeds.error_save'));
        }
    };

    const handleSeedEdit = (seed: Seed) => {
        let bestBeforeStr = '';
        if (seed.bestBefore) {
            // Handle both Firestore Timestamp and Date object, or string
            if (typeof (seed.bestBefore as any).toDate === 'function') {
                bestBeforeStr = getLocalDateString((seed.bestBefore as any).toDate());
            } else if (seed.bestBefore instanceof Date) {
                bestBeforeStr = getLocalDateString(seed.bestBefore);
            }
        }

        setSeedFormData({
            name: seed.name,
            variety: seed.variety || '',
            notes: seed.notes || '',
            plantIndoor: seed.plantIndoor || {},
            plantOutdoors: seed.plantOutdoors || {},
            harvest: seed.harvest || {},
            bestBefore: bestBeforeStr || null,
            supplier: seed.supplier || '',
            count: seed.count !== undefined && seed.count !== null ? String(seed.count) : '',
            rawInputText: seed.rawInput?.text || '',
            imageFile: null,
            imageUrl: seed.imageUrl || null,
        });
        setEditingSeed(seed);
        setSeedTab(1);
        setShowSeedForm(true);
    };

    const handleDeleteSeed = (seed: Seed) => {
        setSeedToDelete(seed);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteSeed = async () => {
        if (seedToDelete) {
            try {
                await deleteDoc(doc(db, 'seeds', seedToDelete.id));
                logEvent(analytics, 'seed_deleted', { seed_id: seedToDelete.id });
            } catch (error) {
                console.error("Error deleting seed:", error);
                alert(t('seeds.error_delete'));
            }
        }
        setShowDeleteConfirm(false);
        setSeedToDelete(null);
    };

    const handleArchiveSeed = async (seed: Seed) => {
        if (!user) return;
        try {
            await updateDoc(doc(db, 'seeds', seed.id), {
                archived: true,
                updatedAt: new Date()
            });
        } catch (error) {
            console.error("Error archiving seed:", error);
            alert(t('seeds.error_archive'));
        }
    };

    const handlePlantSeed = (seed: Seed, initialStage = 'Seedling') => {
        setPlantingSeed(seed);
        setPlantingData({
            spaceId: '',
            count: 1,
            plantingDate: getLocalDateString(),
            stage: initialStage
        });
        setShowPlantingForm(true);
    };

    const handlePlantingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !plantingSeed) return;

        const plantCount = Number(plantingData.count) || 1;
        const plantData = {
            name: plantingSeed.name,
            variety: plantingSeed.variety || '',
            seedId: plantingSeed.id,
            spaceId: plantingData.spaceId,
            count: plantCount,
            stage: plantingData.stage || 'Seedling',
            plantingDate: plantingData.plantingDate ? new Date(plantingData.plantingDate) : new Date(),
            notes: `Planted from seed library.`,
            userId: user.uid,
            imageUrl: plantingSeed.imageUrl || null,
        };

        try {
            const plantRef = await addDoc(collection(db, 'plants'), plantData);
            logEvent(analytics, 'seed_planted', { seed_id: plantingSeed.id, plant_id: plantRef.id, count: plantData.count });

            await createLogEntry({
                userId: user.uid,
                type: 'planted',
                plantId: plantRef.id,
                seedId: plantingSeed.id,
                description: t('logbook.event_planted', { count: plantData.count, name: plantData.variety ? `${plantData.name} (${plantData.variety})` : plantData.name }),
                createdAt: plantingData.plantingDate ? new Date(plantingData.plantingDate) : undefined,
            });

            // Update seed: set lastPlantedAt and manage count/archive
            const seedRef = doc(db, 'seeds', plantingSeed.id);
            const seedCount = plantingSeed.count;

            if (seedCount !== undefined && seedCount !== null) {
                // Known count: decrement or archive
                if (plantCount >= seedCount) {
                    await updateDoc(seedRef, {
                        lastPlantedAt: new Date(),
                        count: 0,
                        archived: true,
                    });
                } else {
                    await updateDoc(seedRef, {
                        lastPlantedAt: new Date(),
                        count: seedCount - plantCount,
                    });
                }
            } else {
                // Unknown count: update lastPlantedAt and ask user what to do
                await updateDoc(seedRef, {
                    lastPlantedAt: new Date(),
                });
                setPostPlantSeed(plantingSeed);
                setShowPostPlantDialog(true);
            }

            // Mark any pending planting suggestions for this seed as 'planted'
            const suggestionsQuery = query(
                collection(db, 'planting_suggestions'),
                where('userId', '==', user.uid),
                where('seedId', '==', plantingSeed.id),
                where('status', '==', 'pending')
            );
            const suggestionsSnapshot = await getDocs(suggestionsQuery);
            if (!suggestionsSnapshot.empty) {
                const batch = writeBatch(db);
                suggestionsSnapshot.docs.forEach(d => {
                    batch.update(d.ref, { status: 'planted', updatedAt: new Date() });
                });
                await batch.commit();
            }

            setShowPlantingForm(false);
        } catch (error) {
            console.error("Error planting seed:", error);
            alert(t('seeds.error_plant'));
        }
    };

    const handlePostPlantAction = async (action: 'archive' | 'keep') => {
        if (!postPlantSeed) return;
        try {
            if (action === 'archive') {
                await updateDoc(doc(db, 'seeds', postPlantSeed.id), { archived: true });
            }
        } catch (error) {
            console.error("Error updating seed:", error);
        }
        setShowPostPlantDialog(false);
        setPostPlantSeed(null);
    };

    const getNextPlantingMonthDistance = useCallback((seed: Seed) => {
        const currentMonth = getCurrentMonthIndex();
        for (let i = 0; i < 12; i++) {
            const m = ((currentMonth - 1 + i) % 12) + 1;
            if (seed.plantIndoor?.[m] || seed.plantOutdoors?.[m]) {
                return i;
            }
        }
        return 12;
    }, [getCurrentMonthIndex]);

    const sortedAndFilteredSeeds = useMemo(() => {
        const currentMonth = getCurrentMonthIndex();

        const filtered = seeds.filter(seed => {
            // Text search using deferred value
            const matchesText =
                (seed.name || '').toLowerCase().includes(deferredSearchQuery.toLowerCase()) ||
                (seed.variety || '').toLowerCase().includes(deferredSearchQuery.toLowerCase());

            return matchesText;
        });

        // Sort seeds
        return [...filtered].sort((a, b) => {
            // 1. Processing and no name
            const aProcessing = !a.processed && !a.name;
            const bProcessing = !b.processed && !b.name;
            if (aProcessing && !bProcessing) return -1;
            if (!aProcessing && bProcessing) return 1;

            // 2. Not planted yet
            const aNotPlanted = !a.lastPlantedAt;
            const bNotPlanted = !b.lastPlantedAt;
            if (aNotPlanted && !bNotPlanted) return -1;
            if (!aNotPlanted && bNotPlanted) return 1;

            if (aNotPlanted && bNotPlanted) {
                // Secondary sort: increasing planting date (distance from now)
                const aDist = getNextPlantingMonthDistance(a);
                const bDist = getNextPlantingMonthDistance(b);
                if (aDist !== bDist) return aDist - bDist;
            }

            // 3. Can be planted right now (In Season)
            const aInSeason = isInSeason(a);
            const bInSeason = isInSeason(b);
            if (aInSeason && !bInSeason) return -1;
            if (!aInSeason && bInSeason) return 1;

            // Tie breaker: Best before date (if existing) or Name
            const getBestBefore = (s: Seed) => {
                if (!s.bestBefore) return null;
                if ((s.bestBefore as any).toDate) return (s.bestBefore as any).toDate();
                return new Date(s.bestBefore as any);
            };

            const aDate = getBestBefore(a);
            const bDate = getBestBefore(b);

            if (aDate && bDate) {
                return aDate.getTime() - bDate.getTime();
            }
            if (aDate) return -1;
            if (bDate) return 1;

            return (a.name || '').localeCompare(b.name || '');
        });
    }, [seeds, deferredSearchQuery, isInSeason, getCurrentMonthIndex, getNextPlantingMonthDistance]);

    if (dataLoading) return <LoadingScreen />;

    return (
        <Box sx={{ flexGrow: 1, pb: 4 }}>
            <Container maxWidth="xl" sx={{ mt: 3 }}>
                <Stack spacing={4}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: { xs: 'stretch', sm: 'center' }
                    }}>
                        <TextField
                            placeholder={t('seeds.search_placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />

                    </Box>
                    <GardenOverview
                        view="seeds"
                        plants={[]}
                        seeds={sortedAndFilteredSeeds}
                        spaces={spaces}
                        onAddSeed={() => setShowSeedForm(true)}
                        onShowSuggestions={() => setShowSuggestions(true)}
                        onEditSeed={handleSeedEdit}
                        onPlantSeed={handlePlantSeed}
                        onDeleteSeed={handleDeleteSeed}
                        onArchiveSeed={handleArchiveSeed}
                        onImageClick={setPreviewImage}
                    />

                    <Box sx={{
                        mt: 6,
                        mb: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {t('seeds.suggestions_prompt')}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<AutoAwesome />}
                            onClick={() => setShowSuggestions(true)}
                            sx={{
                                borderRadius: 4,
                                px: 4,
                                textTransform: 'none',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                    borderColor: 'primary.dark',
                                    bgcolor: 'rgba(var(--primary-rgb), 0.04)'
                                }
                            }}
                        >
                            {t('tasks.get_suggestions_btn')}
                        </Button>
                    </Box>

                    <SeedSuggestions
                        open={showSuggestions}
                        onClose={() => setShowSuggestions(false)}
                        onAddSeed={handleAddSuggestedSeed}
                    />
                </Stack>
            </Container>

            {/* Dialogs */}
            <Dialog open={showSeedForm} onClose={resetSeedForm} maxWidth="sm" fullWidth>
                <DialogTitle>{editingSeed ? t('seeds.edit_seed') : t('seeds.add_seed')}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={seedTab} onChange={(e, v) => setSeedTab(v)} aria-label="seed input mode">
                                <Tab label={t('seeds.ai_auto_fill')} />
                                <Tab label={t('seeds.manual_input')} />
                            </Tabs>
                        </Box>

                        {seedTab === 0 && (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        autoFocus
                                        label={t('seeds.description_label')}
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={seedFormData.rawInputText}
                                        onChange={(e) => setSeedFormData({ ...seedFormData, rawInputText: e.target.value })}
                                        placeholder={t('seeds.description_placeholder')}
                                        sx={{ flex: 1 }}
                                    />
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            sx={{
                                                height: '100%',
                                                minHeight: 125,
                                                borderStyle: 'dashed',
                                                overflow: 'hidden',
                                                p: 0
                                            }}
                                        >
                                            {seedFormData.imageFile ? (
                                                <Box
                                                    component="img"
                                                    src={URL.createObjectURL(seedFormData.imageFile)}
                                                    alt="Preview"
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        opacity: 0.8
                                                    }}
                                                />
                                            ) : (
                                                <Stack alignItems="center">
                                                    <span style={{ fontSize: '2rem' }}>📷</span>
                                                    <span>{t('seeds.upload_image')}</span>
                                                </Stack>
                                            )}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        setSeedFormData({ ...seedFormData, imageFile: e.target.files[0] });
                                                    }
                                                }}
                                            />
                                        </Button>
                                    </Box>
                                </Stack>
                                {seedFormData.imageUrl && !seedFormData.imageFile && (
                                    <Box component="img" src={seedFormData.imageUrl} alt="Seed Packet" sx={{ width: '100%', maxHeight: 200, objectFit: 'contain' }} />
                                )}
                            </Stack>
                        )}

                        {seedTab === 1 && (
                            <>
                                <TextField
                                    autoFocus
                                    label={t('seeds.name_label')}
                                    fullWidth
                                    variant="outlined"
                                    value={seedFormData.name}
                                    onChange={(e) => setSeedFormData({ ...seedFormData, name: e.target.value })}
                                    required
                                />
                                <TextField
                                    label={t('seeds.variety_label')}
                                    fullWidth
                                    variant="outlined"
                                    value={seedFormData.variety}
                                    onChange={(e) => setSeedFormData({ ...seedFormData, variety: e.target.value })}
                                />
                                <SeasonalityInput
                                    value={{
                                        plantIndoor: seedFormData.plantIndoor,
                                        plantOutdoors: seedFormData.plantOutdoors,
                                        harvest: seedFormData.harvest
                                    }}
                                    onChange={(newData) => setSeedFormData({ ...seedFormData, ...newData })}
                                />
                                <TextField label={t('seeds.best_before_label')} type="date" fullWidth InputLabelProps={{ shrink: true }} value={seedFormData.bestBefore || ''} onChange={(e) => setSeedFormData({ ...seedFormData, bestBefore: e.target.value })} />
                                <TextField label={t('seeds.supplier_label')} fullWidth value={seedFormData.supplier} onChange={(e) => setSeedFormData({ ...seedFormData, supplier: e.target.value })} />
                                <TextField label={t('seeds.count_label')} type="number" fullWidth value={seedFormData.count} onChange={(e) => setSeedFormData({ ...seedFormData, count: e.target.value })} helperText={t('seeds.count_helper')} inputProps={{ min: 0 }} />
                                <TextField label={t('seeds.notes_label')} fullWidth multiline rows={4} value={seedFormData.notes} onChange={(e) => setSeedFormData({ ...seedFormData, notes: e.target.value })} />
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={resetSeedForm}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSeedSubmit}>{editingSeed ? t('common.update') : t('common.add')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showPlantingForm} onClose={() => setShowPlantingForm(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('seeds.plant_title', { name: plantingSeed?.variety ? `${plantingSeed.name} (${plantingSeed.variety})` : plantingSeed?.name })}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>{t('seeds.space_label')}</InputLabel>
                            <Select value={plantingData.spaceId} label={t('seeds.space_label')} onChange={(e) => setPlantingData({ ...plantingData, spaceId: e.target.value })}>
                                <MenuItem value="">{t('seeds.space_none')}</MenuItem>
                                {spaces.map(space => (
                                    <MenuItem key={space.id} value={space.id}>{space.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label={t('seeds.num_plants_label')} type="number" fullWidth value={plantingData.count} onChange={(e) => setPlantingData({ ...plantingData, count: e.target.value })} inputProps={{ min: 1, ...(plantingSeed?.count !== undefined && plantingSeed?.count !== null ? { max: plantingSeed.count } : {}) }} helperText={plantingSeed?.count !== undefined && plantingSeed?.count !== null ? t('seeds.seeds_available', { count: plantingSeed.count }) : undefined} />
                        <TextField label={t('seeds.planting_date_label')} type="date" fullWidth InputLabelProps={{ shrink: true }} value={plantingData.plantingDate} onChange={(e) => setPlantingData({ ...plantingData, plantingDate: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setShowPlantingForm(false)}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handlePlantingSubmit}>{t('seeds.plant_now')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showDeleteConfirm}
                onClose={() => { setShowDeleteConfirm(false); setSeedToDelete(null); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('seeds.delete_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('seeds.delete_message', { name: seedToDelete?.variety ? `${seedToDelete.name} (${seedToDelete.variety})` : seedToDelete?.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setShowDeleteConfirm(false); setSeedToDelete(null); }}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={confirmDeleteSeed}>{t('common.delete')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showPostPlantDialog}
                onClose={() => { setShowPostPlantDialog(false); setPostPlantSeed(null); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t('seeds.post_plant_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('seeds.post_plant_message', { name: postPlantSeed?.variety ? `${postPlantSeed.name} (${postPlantSeed.variety})` : postPlantSeed?.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                    <Button variant="contained" onClick={() => handlePostPlantAction('archive')}>{t('seeds.post_plant_archive')}</Button>
                    <Button variant="outlined" onClick={() => handlePostPlantAction('keep')}>{t('seeds.post_plant_keep')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="lg"
                onClick={() => setPreviewImage(null)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        m: 1,
                        maxHeight: 'calc(100% - 16px)',
                        maxWidth: 'calc(100% - 16px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }
                }}
            >
                {previewImage && (
                    <Box
                        component="img"
                        src={previewImage}
                        alt="Preview"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            cursor: 'zoom-out'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage(null);
                        }}
                    />
                )}
            </Dialog>
        </Box>
    );
};

export default SeedsPage;
